import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import React, { type JSX } from 'react';
import { DateiErfolgreichHochgeladenAlert, ERROR_MESSAGE, SUCCESS_MESSAGE_AUTO_HIDE } from '../../components/common/Alert';
import { Dokumententabelle } from '../../components/Dokumententabelle';
import { FileInput } from '../../components/FileInput/FileInput';
import { useVorgangContext } from '../../contexts/vorgangContext';
import { aktionErlaubt } from '../../domain/aktionErlaubt';
import { ladeDateienHoch } from '../../domain/dateiUpload';
import {
  AKTION_DOKUMENT_HOCHLADEN,
  AKTION_DOKUMENT_KATEGORISIEREN,
  AKTION_DOKUMENT_LOESCHEN,
  AKTION_RECHNUNG_KATEGORISIEREN,
  DOKUMENTTYPEN
} from '../../frontendConstants';
import { useUser } from '../../hooks/useUser';
import { DateiKategorie, Dateityp, Unterzeichner } from '../../shared/constants';
import { S3Client } from '../../shared/s3Client';
import { aktualisiereStatus } from '../../shared/util';
import { Datei, Vorgang } from '../../types';
import './Dokumente.css';

export default function Dokumente(): JSX.Element {
  const { vorgang, setLoading, isLoading, aktualisiereVorgang, speichereVorgang } = useVorgangContext();

  const { enqueueSnackbar } = useSnackbar();
  const { gruppenVonMandant } = useUser();

  const darfHochladen = aktionErlaubt(AKTION_DOKUMENT_HOCHLADEN, gruppenVonMandant(vorgang.mandant), vorgang.status) && !isLoading;
  const darfKategorisieren = aktionErlaubt(AKTION_DOKUMENT_KATEGORISIEREN, gruppenVonMandant(vorgang.mandant), vorgang.status) && !isLoading;
  const darfLoeschen = aktionErlaubt(AKTION_DOKUMENT_LOESCHEN, gruppenVonMandant(vorgang.mandant), vorgang.status) && !isLoading;
  const darfRechnungKategorie = aktionErlaubt(AKTION_RECHNUNG_KATEGORISIEREN, gruppenVonMandant(vorgang.mandant), vorgang.status);

  let dokumente: Array<Datei> = [];

  if (vorgang.dateien) {
    dokumente = vorgang.dateien
      .filter((datei) => datei?.typ === Dateityp.DOKUMENT)
      ?.sort((left: Datei, right: Datei) => (right?.dateiname !== left?.dateiname ? -1 : 1));
  }

  const handleDokumentLoeschen = async (dokument: Datei) => {
    try {
      setLoading(true);
      const zuAktualisierendeDateien = await loescheDateien(vorgang, [dokument]);
      void speichereVorgang({
        dateien: zuAktualisierendeDateien
      });
      enqueueSnackbar('Das Dokument wurde erfolgreich gelöscht.', SUCCESS_MESSAGE_AUTO_HIDE);
    } catch (error) {
      console.error({ error });
      enqueueSnackbar('Die Datei konnte nicht gelöscht werden.', ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleKategorieHinzufuegen = (dokument: Datei, kategorie: string, wert?: number | null, unterzeichner?: Unterzeichner) => {
    const zuAktualisierendeDateien = handleKategorienZuDatei(vorgang, dokument, [kategorie], wert, unterzeichner);
    let status = vorgang.status;
    if (kategorie === DateiKategorie.RECHNUNG) {
      status = aktualisiereStatus({ ...vorgang, dateien: zuAktualisierendeDateien });
    }
    if (
      kategorie === DateiKategorie.RECHNUNG &&
      (Array.isArray(vorgang.rechnungsnummern) ? !vorgang.rechnungsnummern.includes((dokument?.dateiname ?? '').replace(/\.[a-zA-Z0-9]+$/, '')) : true)
    ) {
      let rechnungsnummern: string[] = [];
      const rechnungsname = (dokument?.dateiname ?? '').replace(/\.[a-zA-Z0-9]+$/, '');
      if (Array.isArray(vorgang.rechnungsnummern)) {
        rechnungsnummern = [...vorgang.rechnungsnummern.filter((x): x is string => typeof x === 'string'), rechnungsname];
      } else {
        rechnungsnummern = [rechnungsname];
      }
      aktualisiereVorgang({
        rechnungsnummern,
        status,
        dateien: zuAktualisierendeDateien
      });
    } else {
      aktualisiereVorgang({
        status,
        dateien: zuAktualisierendeDateien
      });
    }
  };

  const handleKategorieLoeschen = (dokument: Datei, kategorie: string) => {
    const zuAktualisierendeDateien = handleKategorienZuDatei(vorgang, dokument, [kategorie]);
    let status = vorgang.status;
    if (kategorie === DateiKategorie.RECHNUNG) {
      status = aktualisiereStatus({ ...vorgang, dateien: zuAktualisierendeDateien });
    }
    aktualisiereVorgang({
      status,
      dateien: zuAktualisierendeDateien
    });
  };

  const handleFileUpload = async (files: File[]) => {
    try {
      setLoading(true);
      const zuAktualisierendeDateien = await ladeDateienHoch(vorgang, files);
      void speichereVorgang({
        dateien: zuAktualisierendeDateien
      });
      enqueueSnackbar(<DateiErfolgreichHochgeladenAlert vorgang={vorgang} multiple={files.length > 1} typ={Dateityp.DOKUMENT} />, SUCCESS_MESSAGE_AUTO_HIDE);
    } catch (error) {
      console.error({ error });
      enqueueSnackbar('Das Dokument konnte nicht hochgeladen werden.', ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <div className="dokumente__aktionen">
        <FileInput
          onChange={(acceptedFiles) => void handleFileUpload(acceptedFiles)}
          accept={DOKUMENTTYPEN.join(',')}
          dataTestid="dokumentFileInput"
          disabled={!darfHochladen}
        >
          Dokumente hochladen
        </FileInput>
      </div>
      {dokumente.length > 0 ? (
        <Dokumententabelle
          dokumente={dokumente}
          spalten={['dateiname', 'uploaddatum', 'kategorien']}
          handleDokumentLoeschen={(...args) => void handleDokumentLoeschen(...args)}
          handleKategorieHinzufuegen={handleKategorieHinzufuegen}
          handleKategorieLoeschen={handleKategorieLoeschen}
          darfKategorisieren={darfKategorisieren}
          darfRechnungKategorie={darfRechnungKategorie}
          darfLoeschen={darfLoeschen}
          dataTestId="dokumente"
        />
      ) : (
        <Alert severity="info">Zu diesem Vorgang gibt es keine Dokumente.</Alert>
      )}
    </>
  );
}

export async function loescheDateien(vorgang: Vorgang, dateien: Datei[]): Promise<Datei[]> {
  const zuAktualisierendeDateien = (vorgang.dateien ?? []).filter((d) => !dateien.some((td) => td.key === d.key));
  await Promise.all(
    dateien.map(async (datei) => {
      if (datei.key) {
        await S3Client.remove(datei.key);
      }
    })
  );
  return zuAktualisierendeDateien;
}

export function handleKategorienZuDatei(vorgang: Vorgang, datei: Datei, kategorien: string[], wert?: number | null, unterzeichner?: Unterzeichner): Datei[] {
  return (vorgang.dateien ?? []).map((d) => {
    if (d.key === datei.key) {
      kategorien.forEach((kategorie) => {
        if ((d.kategorien ?? []).includes(kategorie)) {
          d.kategorien = (d.kategorien ?? []).filter((kat) => kat !== kategorie);
        } else {
          (d.kategorien ?? []).push(kategorie);
        }
      });
      if (wert) {
        d.wert = wert;
      }
      if (unterzeichner) {
        d.unterzeichner = unterzeichner;
      }
      return { ...d };
    }
    return d;
  });
}
